import { Sale } from "@/models/base";
import { CalendarEvent, GetColorMethod } from "@/models/calendar-event";
import { CalendarService } from "@/services";
import { i18n } from "@/setup/i18n-setup";
import { isSameDate } from "../date-utils";
import { durationToMinutes, stringToDuration } from "../duration";

export class SaleEditCreateCalendarEventState {
	private sale:Sale = new Sale();
	private successEventHandlers:Function[] = [];
	private errorEventHandlers:Function[] = [];
	public shouldOpenOnSave:boolean = false;

	public planOnNextAvailableFreeSpotOnDate:boolean = false;
	public bindToDeliveryDate:boolean = true;
	public date:Date = new Date();
	public duration:number = stringToDuration("01:00");
	public calendarId:number = 0;
	public title:string = "";
	public isSaving:boolean = false;
	public getColorMethod:GetColorMethod = GetColorMethod.NONE;
	public color:string = "";
	public toEditEvent:CalendarEvent | null = null;

	public get isOpen():boolean {
		return this.toEditEvent != null;
	}

	public async show(sale:Sale):Promise<void>{
		this.title = `${sale.getTypeName(i18n.locale)} ${sale.ComputedFriendlyID}`;
		this.toEditEvent = this.createEvent(sale);
		if (sale.DeliveryDate){
			this.date = sale.DeliveryDate;
		}
		return new Promise((resolve, reject)=>{
			this.successEventHandlers.push(resolve);
			this.errorEventHandlers.push(reject);
		});
	}

	public skip(){
		for (let handler of this.successEventHandlers){
			handler();
		}
		this.errorEventHandlers.splice(0);
		this.successEventHandlers.splice(0);
		this.toEditEvent = null;
	}

	private async savePlanNextAvailbleFreeSpotOnDate(event:CalendarEvent){
		let eventsOfDay = await CalendarService.getEventsByCalendarsInRange([this.calendarId], this.date, this.date);
		let highestEvent:null | CalendarEvent = null;
		let durationMinutes = durationToMinutes(this.duration);
		for (let ev of eventsOfDay) {
			if (!highestEvent) {
				highestEvent = ev;
				continue;
			}
			if (highestEvent.EndTime.getTime() > ev.EndTime.getTime()) {
				continue;
			}
			highestEvent = ev;
		}
		if (highestEvent && !isSameDate(highestEvent.EndTime, event.StartTime)) {
			event.StartTime.setHours(24, -durationMinutes, 0);
		}else if (highestEvent) {
			event.StartTime.setTime(highestEvent.EndTime.getTime());
		}else{
			event.StartTime.setHours(0, 0, 0);
		}
		event.EndTime.setTime(event.StartTime.getTime());
		event.EndTime.setMinutes(event.EndTime.getMinutes() + durationMinutes);
	}

	private createEvent(sale:Sale): CalendarEvent{
		this.sale = sale;
		let event = new CalendarEvent();
		event.Title = this.title;
		event.StartTime = this.date;
		event.SaleID = sale.ID;
		event.SaleBookYear = sale.BookYear;
		event.CalendarID = this.calendarId;
		event.Color = this.color;
		event.applySaleDefaults();
		return event;
	}

	public async save(){
		if (this.isSaving) return;
		this.isSaving = true;
		try{

			let event = new CalendarEvent();
			event.Title = this.title;
			event.StartTime = this.date;
			//event.SaleID = this.sale.ID;
			//event.SaleBookYear = this.sale.BookYear;
			event.CalendarID = this.calendarId;
			event.StartTimeIsLinkedToSaleDeliveryDate = this.bindToDeliveryDate;
			event.Color = this.color;
			event.GetColorMethod = this.getColorMethod;
			if (this.planOnNextAvailableFreeSpotOnDate){
				await this.savePlanNextAvailbleFreeSpotOnDate(event);
			}else{
				event.EndTime = new Date(event.StartTime);
				event.EndTime.setMinutes(event.EndTime.getMinutes()+durationToMinutes(this.duration));
			}
			if (this.bindToDeliveryDate){
				this.sale.DeliveryDate = event.StartTime;
			}
			await CalendarService.postEvents([event]);
			this.skip();
		}catch(err){
		}finally{
			this.isSaving = false;
		}
	}

	public cancel(){
		for (let handler of this.errorEventHandlers){
			handler();
		}
		this.errorEventHandlers.splice(0);
		this.successEventHandlers.splice(0);
		this.toEditEvent = null;
	}

}